import React, { useEffect, useState } from 'react';
import { exitAPI, offboardingAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Loader2, LogOut, AlertTriangle, CheckCircle2, XCircle, Clock, Upload, FileText, Download, Star } from 'lucide-react';

const REASONS = ['Career growth', 'Better opportunity', 'Relocation', 'Personal reasons', 'Health', 'Higher studies', 'Family commitments', 'Other'];
const STATUS_META = {
  pending:   { label: 'Pending HR Decision', color: 'secondary', icon: Clock },
  approved:  { label: 'Approved',            color: 'default',   icon: CheckCircle2 },
  rejected:  { label: 'Rejected',            color: 'destructive', icon: XCircle },
  withdrawn: { label: 'Withdrawn',           color: 'outline',   icon: XCircle },
};

const MyResignation = () => {
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ last_working_day: '', reason: REASONS[0], feedback: '' });
  const [offboarding, setOffboarding] = useState(null);
  const [empId, setEmpId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [interviewForm, setInterviewForm] = useState({
    overall_rating: 4, management_rating: 4, growth_rating: 4, culture_rating: 4, compensation_rating: 4,
    primary_reason: '', would_recommend: true, feedback: '', suggestions: ''
  });
  const [submittingInterview, setSubmittingInterview] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await exitAPI.mine();
      setCurrent(r.data);
      if (r.data && r.data.employee_id && r.data.status === 'approved') {
        setEmpId(r.data.employee_id);
        try {
          const off = await offboardingAPI.get(r.data.employee_id);
          setOffboarding(off.data);
        } catch (e) { /* offboarding may not exist yet */ }
      }
    } catch (e) { /* employee may have no resignation */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.last_working_day || !form.reason) { toast.error('Last working day and reason are required'); return; }
    if (!window.confirm('Submit resignation? Your HR will be notified.')) return;
    setSubmitting(true);
    try {
      await exitAPI.submit(form);
      toast.success('Resignation submitted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Submission failed'); }
    finally { setSubmitting(false); }
  };

  const withdraw = async () => {
    if (!current || current.status !== 'pending') return;
    if (!window.confirm('Withdraw your resignation? This cannot be undone — you will need to re-submit if you change your mind again.')) return;
    try {
      await exitAPI.withdraw(current.id);
      toast.success('Resignation withdrawn');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const completeTask = async (taskKey) => {
    if (!empId) return;
    try {
      await offboardingAPI.updateTask(empId, { task_key: taskKey, status: 'completed' });
      toast.success('Task marked complete');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const uploadKT = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !empId) return;
    setUploading(true);
    try {
      await offboardingAPI.uploadKT(empId, file);
      toast.success('KT document uploaded');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const submitInterview = async () => {
    if (!empId) return;
    if (!interviewForm.primary_reason) { toast.error('Primary reason is required'); return; }
    setSubmittingInterview(true);
    try {
      await offboardingAPI.submitExitInterview(empId, interviewForm);
      toast.success('Thank you for your feedback');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubmittingInterview(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  // If there's an active (pending/approved) resignation, show it
  const isActive = current && ['pending', 'approved'].includes(current.status);

  return (
    <div className="space-y-6 max-w-4xl" data-testid="my-resignation-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><LogOut />My Resignation</h1>
        <p className="text-muted-foreground text-sm">Initiate your separation from the organisation</p>
      </div>

      {isActive ? (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Resignation Status</CardTitle>
            {(() => { const M = STATUS_META[current.status]; const I = M.icon; return (
              <Badge variant={M.color} className="flex items-center gap-1" data-testid="resignation-status-badge"><I size={12} />{M.label}</Badge>
            ); })()}
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-muted-foreground">Submitted</Label><p>{new Date(current.submitted_at).toLocaleString()}</p></div>
              <div><Label className="text-xs text-muted-foreground">Last Working Day</Label><p className="font-semibold">{current.last_working_day}</p></div>
            </div>
            <div><Label className="text-xs text-muted-foreground">Reason</Label><p>{current.reason}</p></div>
            {current.feedback && <div><Label className="text-xs text-muted-foreground">Your feedback</Label><p className="whitespace-pre-wrap">{current.feedback}</p></div>}
            {current.hr_comments && <div className="bg-muted/40 rounded p-3"><Label className="text-xs text-muted-foreground">HR comments</Label><p className="whitespace-pre-wrap">{current.hr_comments}</p></div>}
            {current.status === 'pending' && (
              <div className="pt-3 border-t flex items-center justify-between">
                <p className="text-xs text-muted-foreground flex items-center gap-1"><AlertTriangle size={12} />Awaiting HR decision. You may withdraw any time before they decide.</p>
                <Button variant="outline" size="sm" onClick={withdraw} data-testid="withdraw-btn">Withdraw</Button>
              </div>
            )}
            {current.status === 'approved' && (
              <div className="pt-3 border-t bg-amber-50 dark:bg-amber-950/30 -m-4 p-4 rounded-b text-amber-700 dark:text-amber-300">
                <p className="text-sm flex items-start gap-2"><AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />Your resignation has been accepted. Please complete the offboarding checklist and ensure proper knowledge transfer before <strong>{current.last_working_day}</strong>.</p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader><CardTitle className="text-base">Submit Resignation</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded p-3 text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <span>This action notifies your HR team. After HR approval your access will be revoked on the last working day. Please discuss with your manager before submitting.</span>
            </div>
            <div>
              <Label>Proposed Last Working Day *</Label>
              <Input type="date" value={form.last_working_day} onChange={(e) => setForm({...form, last_working_day: e.target.value})} min={new Date().toISOString().split('T')[0]} data-testid="lwd-input" />
              <p className="text-xs text-muted-foreground mt-1">Subject to notice period per your contract. HR may adjust this date.</p>
            </div>
            <div>
              <Label>Reason for Leaving *</Label>
              <Select value={form.reason} onValueChange={(v) => setForm({...form, reason: v})}>
                <SelectTrigger data-testid="reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>{REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Feedback / Comments (optional)</Label>
              <Textarea value={form.feedback} onChange={(e) => setForm({...form, feedback: e.target.value})} rows={4} placeholder="Any feedback you'd like to share. This will be visible to HR." data-testid="feedback-input" />
            </div>
            <div className="pt-3 border-t flex justify-end">
              <Button onClick={submit} disabled={submitting} variant="destructive" data-testid="submit-resignation-btn">
                {submitting && <Loader2 size={14} className="animate-spin mr-2" />}<LogOut size={14} className="mr-2" />Submit Resignation
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* If most recent is rejected or withdrawn, also show form to allow re-submit */}
      {current && !isActive && (
        <p className="text-sm text-muted-foreground">Your previous resignation was <strong>{current.status}</strong> on {current.decided_at ? new Date(current.decided_at).toLocaleDateString() : '—'}.</p>
      )}

      {/* Offboarding workflow — visible only when resignation approved */}
      {current && current.status === 'approved' && offboarding && (
        <Card data-testid="offboarding-section">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>Offboarding Workflow</span>
              <Badge variant={offboarding.status === 'completed' ? 'default' : 'secondary'}>{offboarding.status}</Badge>
            </CardTitle>
            <div className="space-y-1">
              {(() => {
                const completed = offboarding.tasks.filter(t => t.status === 'completed' || t.status === 'na').length;
                return (
                  <div className="flex items-center gap-2">
                    <Progress value={completed * 100 / offboarding.tasks.length} className="h-2 flex-1" />
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{completed} / {offboarding.tasks.length}</span>
                  </div>
                );
              })()}
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="tasks">
              <TabsList>
                <TabsTrigger value="tasks" data-testid="my-off-tab-tasks">My Tasks</TabsTrigger>
                <TabsTrigger value="kt" data-testid="my-off-tab-kt">KT Upload</TabsTrigger>
                <TabsTrigger value="interview" data-testid="my-off-tab-interview">Exit Interview</TabsTrigger>
              </TabsList>

              <TabsContent value="tasks">
                <div className="space-y-2">
                  {offboarding.tasks.filter(t => t.assigned_role === 'employee').map(t => (
                    <div key={t.key} className="flex items-center justify-between p-3 border rounded">
                      <div className="flex items-center gap-2">
                        {t.status === 'completed' ? <CheckCircle2 size={16} className="text-emerald-600" /> : <Clock size={16} className="text-amber-500" />}
                        <span className="text-sm">{t.label}</span>
                      </div>
                      {t.status !== 'completed' && t.key !== 'exit_interview' && t.key !== 'kt_doc_upload' && (
                        <Button size="sm" variant="outline" onClick={() => completeTask(t.key)} data-testid={`my-complete-${t.key}`}>Mark Done</Button>
                      )}
                    </div>
                  ))}
                  <div className="text-xs text-muted-foreground italic">Other tasks (asset return, access revoke, finance clearance) are managed by HR.</div>
                </div>
              </TabsContent>

              <TabsContent value="kt">
                <div className="space-y-3">
                  <Label htmlFor="kt-file" className="cursor-pointer inline-flex items-center gap-2 px-4 py-3 border-2 border-dashed rounded hover:bg-muted/50">
                    {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    Upload KT document (PDF, DOCX, XLSX, ≤10 MB)
                    <input id="kt-file" type="file" className="hidden" onChange={uploadKT} disabled={uploading} data-testid="kt-upload-input" />
                  </Label>
                  {(offboarding.kt_documents || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">No documents uploaded yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {offboarding.kt_documents.map(d => (
                        <div key={d.file_id} className="flex items-center justify-between border rounded p-2">
                          <div className="flex items-center gap-2"><FileText size={14} /><span className="text-sm">{d.filename}</span><span className="text-xs text-muted-foreground">{(d.size/1024).toFixed(1)} KB</span></div>
                          <span className="text-xs text-muted-foreground">Uploaded {new Date(d.uploaded_at).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="interview">
                {offboarding.exit_interview ? (
                  <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50 rounded p-3 text-sm">
                    <p className="text-emerald-700 dark:text-emerald-300 flex items-center gap-2"><CheckCircle2 size={14} />Thank you! Your exit interview was submitted on {new Date(offboarding.exit_interview.submitted_at).toLocaleDateString()}.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">Your honest feedback helps us improve. All responses are anonymous to your team.</p>
                    {[['overall_rating','Overall experience'],['management_rating','Management'],['growth_rating','Career growth opportunities'],['culture_rating','Company culture'],['compensation_rating','Compensation & benefits']].map(([k,l]) => (
                      <div key={k} className="flex items-center justify-between gap-3">
                        <Label className="flex-1">{l}</Label>
                        <div className="flex gap-1">
                          {[1,2,3,4,5].map(n => (
                            <button key={n} type="button" onClick={() => setInterviewForm({...interviewForm, [k]: n})} className="p-1" data-testid={`rating-${k}-${n}`}>
                              <Star size={20} className={interviewForm[k] >= n ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} />
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                    <div>
                      <Label>Primary reason for leaving *</Label>
                      <Input value={interviewForm.primary_reason} onChange={(e) => setInterviewForm({...interviewForm, primary_reason: e.target.value})} placeholder="e.g., Better opportunity, Relocation, etc." data-testid="interview-primary-reason" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={interviewForm.would_recommend} onCheckedChange={(v) => setInterviewForm({...interviewForm, would_recommend: v})} data-testid="recommend-switch" />
                      <Label>I would recommend this company to others</Label>
                    </div>
                    <div>
                      <Label>Detailed feedback (optional)</Label>
                      <Textarea rows={3} value={interviewForm.feedback} onChange={(e) => setInterviewForm({...interviewForm, feedback: e.target.value})} data-testid="interview-feedback" />
                    </div>
                    <div>
                      <Label>Suggestions for improvement (optional)</Label>
                      <Textarea rows={3} value={interviewForm.suggestions} onChange={(e) => setInterviewForm({...interviewForm, suggestions: e.target.value})} data-testid="interview-suggestions" />
                    </div>
                    <Button onClick={submitInterview} disabled={submittingInterview} data-testid="submit-interview-btn">
                      {submittingInterview && <Loader2 size={14} className="animate-spin mr-2" />}Submit Exit Interview
                    </Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MyResignation;
